import React, { useRef, useState } from 'react';
import ScheduledMessageFormPopup from './ScheduledMessageFormPopup';
import { DraggableData, DraggableEvent } from 'react-draggable';
import { useChatPopupStore, useChatStore } from '@store/chat';
import { chatRoomRemWidth } from 'constants/chat';
import { ScheduledMessage, ScheduledMessageForm } from '@asset/type/chat';
import {
  deleteScheduledMessage,
  getScheduledMessageList,
} from '@apis/api/chat';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import Dialog from '@components/common/modal/Dialog';
import ScheduledMessageListPopup from './ScheduledMessageListPopup';
import { useForm } from 'react-hook-form';
import { ScheduledMessageFormKey } from '@asset/enum/chatEnum';
import { FileInfo } from '@asset/type/file';

const ScheduledMessageSection = ({
  isOpenScheduledMessagePopup,
  isOpenScheduledMessageListPopup,
  isTablet,
  setIsOpenScheduledMessagePopup,
  setIsOpenScheduledMessageListPopup,
  handleDragPopup,
}: {
  isOpenScheduledMessagePopup: boolean;
  isOpenScheduledMessageListPopup: boolean;
  isTablet: boolean;
  setIsOpenScheduledMessagePopup: (visible: boolean) => void;
  setIsOpenScheduledMessageListPopup: (visible: boolean) => void;
  handleDragPopup: (
    e: DraggableEvent,
    data: DraggableData,
    ref: React.RefObject<HTMLDivElement>,
  ) => void;
}) => {
  const { chatPopupRef } = useChatPopupStore();
  const { selectedChannel } = useChatStore();
  const scheduleMessageFormPopupRef = useRef<HTMLDivElement>(null);
  const scheduleMessageListPopupRef = useRef<HTMLDivElement>(null);

  const scheduledMessageForm = useForm<ScheduledMessageForm>({
    mode: 'onChange',
  });

  const [formId, setFormId] = useState<string | number>(null);
  const [files, setFiles] = useState<FileInfo[]>([]);

  const [selectedMessage, setSelectedMessage] =
    useState<ScheduledMessage>(null);
  const [scheduledMessageToDelete, setScheduledMessageToDelete] =
    useState<ScheduledMessage>(null);
  const [openCancelScheduledMessageModal, setOpenCancelScheduledMessageModal] =
    useState<boolean>(false);

  const [isOpenResetModal, setIsOpenResetModal] = useState<boolean>(false);

  const { data: scheduledMessagesList, refetch: refetchScheduledMessagesList } =
    useQuery<ScheduledMessage[]>(
      ['GET_SCHEDULED_MESSAGES', isOpenScheduledMessageListPopup],
      async () => (await getScheduledMessageList()).data,
      { initialData: [], enabled: isOpenScheduledMessageListPopup },
    );

  const handleDeleteScheduledMessage = async () => {
    await deleteScheduledMessage(scheduledMessageToDelete.id)
      .then(() => {
        if (scheduledMessageToDelete.id === selectedMessage?.id) {
          setSelectedMessage(null);
        }
        refetchScheduledMessagesList();
        toast.success('예약 메시지가 삭제되었습니다.');
      })
      .catch(() => {
        toast.error('예약 메시지 삭제에 실패했습니다.');
      });
    setOpenCancelScheduledMessageModal(false);
    setScheduledMessageToDelete(null);
  };

  const handleDragScheduledMessageFormPopup = (
    e: DraggableEvent,
    data: DraggableData,
  ) => {
    if (scheduleMessageFormPopupRef && scheduleMessageFormPopupRef.current) {
      handleDragPopup(e, data, scheduleMessageFormPopupRef);
    }
  };

  const handleDragScheduledMessageListPopup = (
    e: DraggableEvent,
    data: DraggableData,
  ) => {
    if (scheduleMessageListPopupRef && scheduleMessageListPopupRef.current) {
      handleDragPopup(e, data, scheduleMessageListPopupRef);
    }
  };

  const resetForm = () => {
    scheduledMessageForm.reset();
    setSelectedMessage(null);
    setFormId(crypto.randomUUID());
    setFiles([]);
  };

  return (
    <>
      {isOpenScheduledMessagePopup && (
        <ScheduledMessageFormPopup
          key={formId}
          popupRef={scheduleMessageFormPopupRef}
          defaultPosition={{
            x: scheduleMessageFormPopupRef?.current
              ? `${scheduleMessageFormPopupRef?.current?.offsetLeft}px`
              : chatPopupRef?.current
              ? `${
                  chatPopupRef?.current?.offsetLeft -
                  chatRoomRemWidth * 16 +
                  -10
                }px`
              : null,
            y: scheduleMessageFormPopupRef?.current
              ? `${scheduleMessageFormPopupRef?.current?.offsetTop}px`
              : chatPopupRef?.current
              ? `${chatPopupRef?.current?.offsetTop}px`
              : null,
          }}
          scheduledMessageForm={scheduledMessageForm}
          selectedMessage={selectedMessage}
          isTablet={isTablet}
          onDrag={handleDragScheduledMessageFormPopup}
          togglePopupVisible={(visible: boolean) => {
            setIsOpenScheduledMessagePopup(visible);
            if (!visible) {
              setSelectedMessage(null);
            }
          }}
          openScheduledMessageListPopup={() => {
            setIsOpenScheduledMessageListPopup(true);
          }}
          onSuccessRegisterScheduledMessage={() => {
            if (isOpenScheduledMessageListPopup) {
              refetchScheduledMessagesList();
            }
            setSelectedMessage(null);
            setFormId(crypto.randomUUID());
          }}
          onDeleteScheduledMessage={(message) => {
            setScheduledMessageToDelete(message);
            setOpenCancelScheduledMessageModal(true);
          }}
          files={files}
          setFiles={setFiles}
        />
      )}
      {isOpenScheduledMessageListPopup && (
        <ScheduledMessageListPopup
          popupRef={scheduleMessageListPopupRef}
          defaultPosition={{
            x: scheduleMessageListPopupRef?.current
              ? `${scheduleMessageListPopupRef?.current?.offsetLeft}px`
              : scheduleMessageFormPopupRef
              ? `${
                  scheduleMessageFormPopupRef?.current?.offsetLeft -
                  chatRoomRemWidth * 16 +
                  -10
                }px`
              : null,
            y: scheduleMessageListPopupRef?.current
              ? `${scheduleMessageListPopupRef?.current?.offsetTop}px`
              : scheduleMessageFormPopupRef
              ? `${scheduleMessageFormPopupRef?.current?.offsetTop}px`
              : null,
          }}
          isTablet={isTablet}
          messagesList={scheduledMessagesList}
          handleDrag={handleDragScheduledMessageListPopup}
          onSelectMessage={(message) => {
            if (!isOpenScheduledMessagePopup) {
              setIsOpenScheduledMessagePopup(true);
            }
            setSelectedMessage(message);
            setFormId(message.id);
          }}
          onDeleteMessage={(message) => {
            setScheduledMessageToDelete(message);
            setOpenCancelScheduledMessageModal(true);
          }}
          onClosePopup={() => setIsOpenScheduledMessageListPopup(false)}
          onClickResetMessageFormButton={() => {
            if (!isOpenScheduledMessagePopup) {
              setIsOpenScheduledMessagePopup(true);
              return;
            }
            const formValues = Object.fromEntries(
              Object.entries(scheduledMessageForm.getValues()).filter(
                ([key]) => key !== ScheduledMessageFormKey.RECIPIENT,
              ),
            );
            const hasContent = Object.values(formValues).some(
              (value) => value !== undefined && value !== null && value !== '',
            );
            const hasFiles = files.length > 0;
            const isRecipientSameAsOpenedChannel =
              scheduledMessageForm.getValues(ScheduledMessageFormKey.RECIPIENT)
                ?.channelUrl === selectedChannel?.url;

            if (hasContent || hasFiles || !isRecipientSameAsOpenedChannel) {
              setIsOpenResetModal(true);
            }
          }}
        />
      )}

      {openCancelScheduledMessageModal && (
        <Dialog
          title={'예약 메시지 삭제'}
          content={['예약 메시지를 삭제하시겠습니까?']}
          buttons={[
            {
              color: 'gray',
              assetType: 'Secondary',
              content: '취소',
              onClickEvent: () => setOpenCancelScheduledMessageModal(false),
            },
            {
              color: 'red',
              assetType: 'Primary',
              content: '삭제하기',
              onClickEvent: () => handleDeleteScheduledMessage(),
            },
          ]}
        />
      )}
      {isOpenResetModal && (
        <Dialog
          title={'새로 예약하기'}
          content={[
            '작성중인 내용을 삭제하고',
            '새로운 예약 메시지를 작성합니다.',
          ]}
          buttons={[
            {
              color: 'gray',
              assetType: 'Secondary',
              content: '취소',
              onClickEvent: () => setIsOpenResetModal(false),
            },
            {
              color: 'blue',
              assetType: 'Primary',
              content: '확인',
              onClickEvent: () => {
                setIsOpenResetModal(false);
                resetForm();
              },
            },
          ]}
        />
      )}
    </>
  );
};

export default ScheduledMessageSection;
